import React from "react";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import { translate } from "../../IntlProviderWrapper";
import { getPlanningDetailsWidth, zoomGanttChart } from "./reducers/properties";
import { ZoomMenu } from "./ZoomMenu";

import type { ReduxState, ActionTypes } from "./reducers/reducers";
import type { GanttChartSourceTypes } from "./reducers/properties";

const MIN_SLIDER_POSITION = 0;
const MAX_SLIDER_POSITION = 100;
const SLIDER_STEP = ZoomMenu.ZOOM_STEP_PERCENT;

type SliderRangeConfig = {
    minValue: number;
    maxValue: number;
    minPosition?: number;
    maxPosition?: number;
};

const getScale = ({
    minValue,
    maxValue,
    minPosition = MIN_SLIDER_POSITION,
    maxPosition = MAX_SLIDER_POSITION,
}: SliderRangeConfig) => {
    const minLogValue = Math.log(minValue);
    const maxLogValue = Math.log(maxValue);
    return {
        minLogValue,
        minPosition,
        scale: (maxLogValue - minLogValue) / (maxPosition - minPosition),
    };
};

const sliderPositionToValue = (position: number, config: SliderRangeConfig): number => {
    const { minLogValue, scale, minPosition } = getScale(config);
    return Math.exp(minLogValue + scale * (position - minPosition));
};

const valueToSliderPosition = (value: number, config: SliderRangeConfig): number => {
    const { minLogValue, scale, minPosition } = getScale(config);
    return minPosition + (Math.log(value) - minLogValue) / scale;
};

type OwnProps = {
    source_type: GanttChartSourceTypes;
    line_group_uuid?: string;
    onClose: () => void;
};

type Props = OwnProps & ReduxProps;

class ZoomDropdownMenu extends React.Component<Props> {
    getZoomContext = () => {
        return {
            minDayWidth: this.props.properties.min_day_width,
            planningDetailsWidth: getPlanningDetailsWidth(this.props.properties.space_for_label),
        };
    };

    getZoomRange = () => {
        return ZoomMenu.getZoomRange(this.getZoomContext());
    };

    getCurrentZoomValue = () => {
        return this.props.properties.width / this.props.properties.days;
    };

    onChangeSlider = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.currentTarget instanceof HTMLInputElement) {
            const { minValue, maxValue } = this.getZoomRange();
            const dayWidth = sliderPositionToValue(parseFloat(event.currentTarget.value), {
                minValue,
                maxValue,
            });
            this.applyZoom(dayWidth);
        }
    };

    applyZoom = (dayWidth: number) => {
        const constrainedDayWidth = ZoomMenu.constrainDayWidth(dayWidth, this.getZoomRange());
        zoomGanttChart(constrainedDayWidth, this.props.source_type, this.props.reduxDispatch);
    };

    onClickMonth = () => {
        this.applyZoom(ZoomMenu.zoomToMonth(this.getZoomContext()));
    };

    onClickWeek = () => {
        this.applyZoom(ZoomMenu.zoomToWeek(this.getZoomContext()));
    };

    onClickDay = () => {
        this.applyZoom(ZoomMenu.zoomToDay(this.getZoomContext()));
    };

    onClickShift = () => {
        this.applyZoom(ZoomMenu.zoomToShift(this.getZoomContext()));
    };

    getSliderValue = () => {
        const value = this.getCurrentZoomValue();
        const { minValue, maxValue } = this.getZoomRange();
        return valueToSliderPosition(value, { minValue, maxValue });
    };

    onClickZoomOut = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const value = ZoomMenu.zoomOut(this.getCurrentZoomValue(), this.getZoomRange());
        this.applyZoom(value);
    };

    onClickZoomIn = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const value = ZoomMenu.zoomIn(this.getCurrentZoomValue(), this.getZoomRange());
        this.applyZoom(value);
    };

    onReset = () => {
        this.onClickMonth();
        this.props.onClose();
    };

    render() {
        const sliderValue = this.getSliderValue();

        return (
            <div className="zoom-menu d-flex flex-column gap-1">
                <div className="close">
                    <i onClick={this.props.onClose} className="fas fa-times"></i>
                </div>
                <div className="d-flex pt-2 pb-3">
                    <button className="circle-btn mr-1" onClick={this.onClickZoomOut}>
                        <i className="fas fa-minus-circle fa-lg" />
                    </button>
                    <input
                        type="range"
                        min={SLIDER_STEP}
                        max={MAX_SLIDER_POSITION}
                        value={sliderValue}
                        step={SLIDER_STEP}
                        onChange={this.onChangeSlider}
                    ></input>
                    <button className="circle-btn ml-1" onClick={this.onClickZoomIn}>
                        <i className="fas fa-plus-circle fa-lg" />
                    </button>
                </div>
                <div className="d-flex flex-column gap-1">
                    <button className="btn btn-light text-uppercase" onClick={this.onClickMonth}>
                        {translate("Calendar.month", "Month")}
                    </button>
                    <button className="btn btn-light text-uppercase" onClick={this.onClickWeek}>
                        {translate("common.week", "Week")}
                    </button>
                    <button className="btn btn-light text-uppercase" onClick={this.onClickDay}>
                        {translate("common.day", "Day")}
                    </button>
                    <button className="btn btn-light text-uppercase" onClick={this.onClickShift}>
                        {translate("common.shift", "Shift")}
                    </button>
                </div>

                <div className="d-flex gap-2 mt-3">
                    <button className="btn btn-outline-danger" onClick={this.onReset}>
                        <i className="fas fa-sync-alt mr-1" />
                        {translate("common.reset", "Reset")}
                    </button>
                    <button className="btn btn-primary" onClick={this.props.onClose}>
                        <i className="fas fa-times mr-1" />
                        {translate("common.close", "Close")}
                    </button>
                </div>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const properties = state.gantt_chart_properties;
    return {
        planning_details_width: properties.width,
        planning_table_days: properties.days,
        properties,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(ZoomDropdownMenu);
